import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, merge, Subject } from 'rxjs';
import { MatSort, SortDirection } from '@angular/material/sort';
import { AccountManagerDataSource } from '../account-manager.data-source';
import { AccountManagerService } from '../account-manager.service';
import { NotificationService } from '../../shared/service/notification.service';
import {
  RemoveAccountManagerDialogComponent,
} from '../remove-account-manager-dialog/remove-account-manager-dialog.component';

@Component({
  selector: 'app-account-manager-overview',
  templateUrl: './account-manager-overview.component.html',
  styleUrls: ['./account-manager-overview.component.scss']
})
export class AccountManagerOverviewComponent implements AfterViewInit, OnDestroy, OnInit {
  private searchInputSubject = new Subject<string>();
  public searchInput = this.searchInputSubject.asObservable();
  public searchText = '';
  private searchEmpty = true;
  public tableColumns: string[] = [
    'name',
    'createdAt',
    'actions'
  ];

  public sortActiveDefault = 'name';
  public sortDirectionDefault: SortDirection = 'asc';
  public pageSizeOptions: number[] = [15, 30, 50];
  public totalItems = 0;
  public dataSource: AccountManagerDataSource;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private accountManagerService: AccountManagerService,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {
    this.dataSource = new AccountManagerDataSource(this.accountManagerService);

    this.dataSource.dataLength.subscribe((total) => {
      this.totalItems = total
    });
  }

  ngOnInit() {
    this.loadData();
  }

  ngAfterViewInit() {
    // Reset pagination after sort change.
    this.sort.sortChange
      .subscribe(() => this.paginator.pageIndex = 0);

    // Load data when a sort or paginator event is emitted.
    merge(this.sort.sortChange, this.paginator.page)
      .subscribe(() => this.loadData());

    // Load data when search input value changes.
    this.searchInput
      .pipe(
        debounceTime(150)
      )
      .subscribe((value) => {
        if (!this.searchEmpty || value.length) {
          this.paginator.pageIndex = 0;
          this.loadData()
        }

        this.searchEmpty = value.length === 0;
      });
  }

  ngOnDestroy() {
    this.searchInputSubject.unsubscribe();
  }

  public onSearch(): void {
    this.searchInputSubject.next(this.searchText);
  }

  public onSearchClear(): void {
    this.searchText = '';
    this.onSearch();
  }

  public onRemoveAction(id: string, name: string): void {
    if (this.dialog.openDialogs.length) {
      return;
    }

    this.dialog
      .open(RemoveAccountManagerDialogComponent, {
        data: {
          id: id,
          name: name,
        },
      })
      .afterClosed()
      .subscribe(
        data => {
          if (data?.id) {
            this.deleteAccountManager(data.id);
          }
        }
      );
  }

  private deleteAccountManager(accountManagerId: string): void {
    this.accountManagerService
      .delete(accountManagerId)
      .subscribe({
        next: () => {
          this.notificationService.success_ts('account_manager.deleted');
          this.loadData();
        }
      })
  }

  private loadData() {
    this.dataSource.loadAccountManagers(
      this.paginator?.pageIndex + 1 || 1,
      this.paginator?.pageSize || this.pageSizeOptions[0],
      this.sort?.active || this.sortActiveDefault,
      this.sort?.direction || this.sortDirectionDefault,
      this.searchText
    );
  }
}
